import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AddressesSkeleton from "../components/AddressesSkeleton";

const Container = styled.div`
  /* min-height: 100vh; */
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  height: 60vh;
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #000;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  &:hover {
    background: #f0f0f0;
    border-radius: 50%;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  margin-top: 1rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const AddressCard = styled.div`
  border: 2px solid #eee;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    border-color: #ddd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const AddressCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddressName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DefaultBadge = styled.span`
  font-size: 11px;
  background: #e3f2fd;
  color: #1565c0;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 12px;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    border-color: #000;
    background: #f9f9f9;
  }

  &.delete:hover {
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const AddressDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AddressDetailItem = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const AddNewAddressBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  border: 2px dashed #ddd;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  color: #000;
  font-size: 14px;

  &:hover {
    border-color: #000;
    background: #f9f9f9;
  }
`;

const AddressForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${(props) => props.fullWidth && `grid-column: 1 / -1;`}

  label {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }

  input,
  textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.3s;

    &:focus {
      outline: none;
      border-color: #000;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const FormActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  button {
    padding: 10px 20px;
    border-radius: 4px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }
`;

const CancelBtn = styled.button`
  background: #f0f0f0;
  color: #000;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveBtn = styled.button`
  background: #000;
  color: #fff;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalDialog = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 20px 0;
    color: #000;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const EmptyContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
`;

const DeleteConfirmDialog = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 90%;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: #d32f2f;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 12px;

    &:before {
      content: "⚠";
      font-size: 24px;
    }
  }

  p {
    font-size: 14px;
    color: #555;
    line-height: 1.6;
    margin: 0 0 24px 0;
  }
`;

const DeleteConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const DeleteCancelBtn = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  &:hover:not(:disabled) {
    background: #e8e8e8;
    border-color: #bbb;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeleteConfirmBtn = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: #d32f2f;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  &:hover:not(:disabled) {
    background: #b71c1c;
    box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

// Indian States List
const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

// Validation Functions
const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone);
};

const validatePincode = (pincode) => {
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode);
};

const validateForm = (data) => {
  const errors = {};

  if (!data.name || data.name.trim() === "") {
    errors.name = "Name is required";
  }

  if (!data.phone || data.phone.trim() === "") {
    errors.phone = "Phone number is required";
  } else if (!validatePhone(data.phone)) {
    errors.phone = "Phone number must be exactly 10 digits";
  }

  if (!data.address || data.address.trim() === "") {
    errors.address = "Address is required";
  }

  if (!data.city || data.city.trim() === "") {
    errors.city = "City is required";
  }

  if (!data.state || data.state.trim() === "") {
    errors.state = "State is required";
  }

  if (!data.pincode || data.pincode.trim() === "") {
    errors.pincode = "Pincode is required";
  } else if (!validatePincode(data.pincode)) {
    errors.pincode = "Pincode must be exactly 6 digits";
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

const Addresses = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    is_default: false,
  });

  // Fetch addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/addresses`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          const mappedAddresses = data.data.map((item) => ({
            id: item.id,
            name: item.name,
            street: item.address,
            city: item.city,
            state: item.state,
            pincode: item.pincode,
            phone: item.phone,
            country: item.country,
            is_default: item.is_default,
          }));
          setSavedAddresses(mappedAddresses);
        } else {
          setSavedAddresses([]);
        }
      } catch (err) {
        console.error("Error fetching addresses:", err);
        setError("Failed to load addresses. Please try again.");
        setSavedAddresses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token, API_BASE_URL, navigate]);

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      is_default: false,
    });
    setFormError(null);
  };

  const handleAddNew = () => {
    setEditing(null);
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (address) => {
    setEditing(address.id);
    setFormData({
      name: address.name,
      phone: address.phone,
      address: address.street,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      country: address.country,
      is_default: address.is_default,
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm(formData);
    if (validationErrors) {
      const errorMessage = Object.values(validationErrors).join("\n");
      setFormError(errorMessage);
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      const url = editing
        ? `${API_BASE_URL}/addresses/${editing}`
        : `${API_BASE_URL}/addresses`;

      const method = editing ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country,
          is_default: formData.is_default,
        }),
      });

      const data = await response.json();

      if (data.status || response.ok) {
        if (editing) {
          // Update existing address
          setSavedAddresses((prev) =>
            prev.map((addr) =>
              addr.id === editing
                ? {
                    id: editing,
                    name: formData.name,
                    street: formData.address,
                    city: formData.city,
                    state: formData.state,
                    pincode: formData.pincode,
                    phone: formData.phone,
                    country: formData.country,
                    is_default: formData.is_default,
                  }
                : addr,
            ),
          );
          setSuccessMessage("Address updated successfully!");
        } else {
          // Add new address
          const newAddress = {
            id: data.data?.id || Date.now(),
            name: formData.name,
            street: formData.address,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
            phone: formData.phone,
            country: formData.country,
            is_default: formData.is_default,
          };
          setSavedAddresses((prev) => [newAddress, ...prev]);
          setSuccessMessage("Address added successfully!");
        }

        setShowForm(false);
        resetForm();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setFormError(
          data.message || "Failed to save address. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error saving address:", err);
      setFormError("Failed to save address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = (addressId) => {
    setDeleteConfirm(addressId);
  };

  const handleDelete = async (addressId) => {
    setDeleting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/addresses/${addressId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status || response.ok) {
        setSavedAddresses((prev) =>
          prev.filter((addr) => addr.id !== addressId),
        );
        setDeleteConfirm(null);
        setSuccessMessage("Address deleted successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.message || "Failed to delete address. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting address:", err);
      setError("Failed to delete address. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };

  return (
    <Container>
      {/* <Navbar /> */}

      <Wrapper>
        <Header>
          <BackButton onClick={() => navigate("/my-account")}>
            <ArrowLeftOutlined />
          </BackButton>
          <PageTitle>Your Addresses</PageTitle>
        </Header>

        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <AddressesSkeleton count={3} />
        ) : savedAddresses.length === 0 && !showForm ? (
          <EmptyContainer>
            <EmptyMessage>
              You don't have any addresses yet. Add your first address to get
              started.
            </EmptyMessage>
            <AddNewAddressBtn onClick={handleAddNew}>
              <PlusOutlined />
              Add New Address
            </AddNewAddressBtn>
          </EmptyContainer>
        ) : (
          <>
            {showForm && (
              <AddressForm onSubmit={handleSave}>
                <h2
                  style={{
                    gridColumn: "1 / -1",
                    fontSize: "18px",
                    fontWeight: "700",
                    margin: "0 0 20px 0",
                    color: "#000",
                  }}
                >
                  {editing ? "Edit Address" : "Add New Address"}
                </h2>

                {formError && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      background: "#f8d7da",
                      color: "#721c24",
                      padding: "12px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      marginBottom: "10px",
                    }}
                  >
                    {formError}
                  </div>
                )}

                <FormGroup>
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., Home, Office"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="Enter 10-digit phone number"
                    maxLength="10"
                    inputMode="numeric"
                    required
                  />
                  {formData.phone && !validatePhone(formData.phone) && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#d32f2f",
                        marginTop: "4px",
                      }}
                    >
                      Phone number must be exactly 10 digits
                    </span>
                  )}
                </FormGroup>

                <FormGroup fullWidth>
                  <label>Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    placeholder="Enter street address, house no., building name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleFormChange}
                    placeholder="Enter city"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label>State *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleFormChange}
                    required
                    style={{
                      padding: "10px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      transition: "border-color 0.3s",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                    }}
                  >
                    <option value="">Select your state</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleFormChange}
                    placeholder="Enter 6-digit pincode"
                    maxLength="6"
                    inputMode="numeric"
                    required
                  />
                  {formData.pincode && !validatePincode(formData.pincode) && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#d32f2f",
                        marginTop: "4px",
                      }}
                    >
                      Pincode must be exactly 6 digits
                    </span>
                  )}
                </FormGroup>

                <FormGroup>
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleFormChange}
                    placeholder="Enter country"
                  />
                </FormGroup>

                <FormGroup fullWidth>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "500",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="is_default"
                      checked={formData.is_default}
                      onChange={handleFormChange}
                      style={{ width: "auto", cursor: "pointer" }}
                    />
                    Set as default address
                  </label>
                </FormGroup>

                <FormActions>
                  <CancelBtn
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </CancelBtn>
                  <SaveBtn type="submit" disabled={saving}>
                    {saving
                      ? "Saving..."
                      : editing
                        ? "Update Address"
                        : "Add Address"}
                  </SaveBtn>
                </FormActions>
              </AddressForm>
            )}

            {!showForm && (
              <AddNewAddressBtn onClick={handleAddNew}>
                <PlusOutlined />
                Add New Address
              </AddNewAddressBtn>
            )}

            {savedAddresses.length > 0 && (
              <AddressGrid>
                {savedAddresses.map((address) => (
                  <AddressCard key={address.id}>
                    <AddressCardHeader>
                      <AddressName>
                        {address.name}
                        {address.is_default && (
                          <DefaultBadge>Default</DefaultBadge>
                        )}
                      </AddressName>
                    </AddressCardHeader>

                    <AddressDetails>
                      <AddressDetailItem>{address.street}</AddressDetailItem>
                      <AddressDetailItem>
                        {address.city}, {address.state} {address.pincode}
                      </AddressDetailItem>
                      <AddressDetailItem>{address.country}</AddressDetailItem>
                      <AddressDetailItem>{address.phone}</AddressDetailItem>
                    </AddressDetails>

                    <ActionButtons>
                      <ActionButton onClick={() => handleEdit(address)}>
                        <EditOutlined />
                        Edit
                      </ActionButton>
                      <ActionButton
                        className="delete"
                        onClick={() => handleDeleteConfirm(address.id)}
                      >
                        <DeleteOutlined />
                        Delete
                      </ActionButton>
                    </ActionButtons>
                  </AddressCard>
                ))}
              </AddressGrid>
            )}

            {/* Delete Confirmation Modal */}
            <ModalOverlay open={deleteConfirm !== null}>
              <DeleteConfirmDialog>
                <h2>Delete Address</h2>
                <p>
                  Are you sure you want to delete this address? This action
                  cannot be undone.
                </p>
                <DeleteConfirmActions>
                  <DeleteCancelBtn
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </DeleteCancelBtn>
                  <DeleteConfirmBtn
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </DeleteConfirmBtn>
                </DeleteConfirmActions>
              </DeleteConfirmDialog>
            </ModalOverlay>
          </>
        )}
      </Wrapper>

      <Footer />
    </Container>
  );
};

export default Addresses;
